import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { phoneValidator } from '@boldpenguin/emperor-form-fields';
import {
  EmperorDialogRefService,
  EmperorThemeColor,
  EmperorUIButtonTypes,
} from '@boldpenguin/emperor-presentational';
import { selectUser } from '@boldpenguin/sdk';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { combineLatest, filter, map, switchMap } from 'rxjs';
import { BpSdkStoreService } from 'src/app/core/services/bp-sdk-store-service/bp-sdk-store.service';
import { IntercomInternalLabels } from 'src/app/core/services/intercom-service/intercom.service';
import {
  selectUserGroupsLoaded,
  selectViewableUserGroupsFormOptions,
} from 'src/app/core/store/user-groups/user-groups.selectors';
import { UserActivationChangeDialogComponent } from 'src/app/features/settings/components/user-list/user-activation-change-dialog/user-activation-change-dialog.component';
import { ITerminalUser } from 'src/app/features/settings/models/user.model';
import { DeactivateUserAction } from 'src/app/features/settings/store/user-settings/users.actions';
import { matchingFieldValidator } from 'src/app/features/signup/utilities/validators';
import { Validators as SharedValidators } from 'src/app/shared/utilities/validators';
import {
  IEmperorUserGroup,
  IUserDetails,
  IUserDetailsFormGroup,
  stringifiedUserGroup,
} from '../../models/user-details.model';

@UntilDestroy()
@Component({
  selector: 'app-add-user-form',
  templateUrl: './add-user-form.component.html',
  styleUrls: ['./add-user-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddUserFormComponent implements OnInit {
  @Input() preventAccountTypeChange = false;
  @Input() existingUser?: ITerminalUser | null;
  @Input() submitButtonText: string;
  @Input() showClearButton = false;
  @Output() submitClick: EventEmitter<IUserDetails> = new EventEmitter();

  userDetailsForm: FormGroup<IUserDetailsFormGroup>;
  emperorUserGroups: IEmperorUserGroup[];
  placeholderPhoneNumber = '';
  formBuilder = inject(NonNullableFormBuilder);
  phoneMask = {
    mask: '(000) 000-0000',
  };
  readonly intercomLabels = IntercomInternalLabels;
  readonly EmperorUIButtonTypes = EmperorUIButtonTypes;
  readonly EmperorThemeColor = EmperorThemeColor;

  private store = inject(Store);
  private dialogService = inject(EmperorDialogRefService);
  private bpSdkStoreService = inject(BpSdkStoreService);

  ngOnInit() {
    this.setUpForm();
    this.userDetailsForm.controls.email.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() =>
        this.userDetailsForm.controls.emailConfirmation.updateValueAndValidity(),
      );

    this.store
      .pipe(
        select(selectUserGroupsLoaded),
        filter(Boolean),
        switchMap(() =>
          combineLatest([
            this.store.pipe(select(selectViewableUserGroupsFormOptions)),
            this.bpSdkStoreService.state$.pipe(
              select(selectUser),
              filter(Boolean),
              map((user) => user.info.phone),
            ),
          ]),
        ),
        untilDestroyed(this),
      )
      .subscribe(([userGroupsForForm, phone]) => {
        this.emperorUserGroups = userGroupsForForm;
        this.placeholderPhoneNumber = phone ?? '';

        this.setFormValues();
      });
  }

  submit() {
    if (this.userDetailsForm.invalid) {
      // Without resetting the form, the emperor error handler was
      // not getting triggered on statusChange after unchecking skip
      this.userDetailsForm.reset(this.userDetailsForm.value);
      this.userDetailsForm.markAllAsTouched();
    } else {
      this.submitClick.emit(this.userDetailsForm.getRawValue());
      this.clearForm();
    }
  }

  deactivateUser(user: ITerminalUser): void {
    this.dialogService.open(UserActivationChangeDialogComponent, {
      data: {
        activate: false,
        confirmAction: DeactivateUserAction({ user }),
      },
    });
  }

  clearForm() {
    this.userDetailsForm.reset();
    this.setFormValues();
    this.focusFirstField();
  }

  private focusFirstField() {
    const field = document.querySelector<HTMLElement>(
      '#first-field .mat-mdc-form-field input',
    );
    field?.focus();
  }

  private setUpForm() {
    this.userDetailsForm = this.formBuilder.group({
      firstName: [
        '',
        {
          validators: [Validators.required, Validators.maxLength(255)],
          nonNullable: true,
        },
      ],
      lastName: [
        '',
        {
          validators: [Validators.required, Validators.maxLength(255)],
          nonNullable: true,
        },
      ],
      phone: [
        '',
        {
          validators: [
            Validators.required,
            Validators.minLength(10),
            Validators.maxLength(10),
            phoneValidator,
          ],
        },
      ],
      email: [
        '',
        {
          validators: [
            Validators.required,
            Validators.email,
            SharedValidators.groupEmailValidator,
          ],
          nonNullable: true,
        },
      ],
      emailConfirmation: [
        '',
        {
          validators: [Validators.required],
          nonNullable: true,
        },
      ],
      role: [
        '' as stringifiedUserGroup,
        {
          validators: [Validators.required],
          nonNullable: true,
        },
      ],
    });

    this.userDetailsForm.controls.emailConfirmation.addValidators([
      matchingFieldValidator(
        this.userDetailsForm.controls.emailConfirmation,
        this.userDetailsForm.controls.email,
      ),
    ]);

    // Prevent the last active principal from changing their account type
    if (this.preventAccountTypeChange) {
      this.userDetailsForm.get('role')?.disable();
    }
  }

  private setFormValues() {
    const initialValues = {
      firstName: this.existingUser?.first_name || '',
      lastName: this.existingUser?.last_name || '',
      phone:
        this.existingUser?.phone?.replace(/\D/g, '') ||
        this.placeholderPhoneNumber.replace(/\D/g, ''),
      email: this.existingUser?.email || '',
      emailConfirmation: '',
      role: this.getInitialRole(),
    };

    this.userDetailsForm.setValue(initialValues);
  }

  private getInitialRole(): stringifiedUserGroup {
    const defaultUserGroup = this.emperorUserGroups
      .map((group) => ({ id: group.id, name: group.label }))
      .find((userGroup) => userGroup.name === 'Principal') ?? {
      id: '',
      name: '',
    };

    return JSON.stringify(
      this.existingUser?.primary_user_group
        ? this.existingUser.primary_user_group
        : defaultUserGroup,
    ) as stringifiedUserGroup;
  }
}
